import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, ScanLine, Sigma } from "lucide-react";
import { api } from "@/lib/ocorenApi";

const Stat = ({ label, value, accent = "default" }) => {
  const colors = {
    default: "text-[#09090B]",
    success: "text-[#10B981]",
    warning: "text-[#F59E0B]",
    error: "text-[#F43F5E]",
  };
  return (
    <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-md px-3 py-2.5">
      <div className="text-[9px] uppercase tracking-[0.15em] text-[#52525B] font-bold">
        {label}
      </div>
      <div
        className={`text-xl font-bold font-mono-data mt-0.5 ${colors[accent]}`}
      >
        {value}
      </div>
    </div>
  );
};

export const AvaliacaoModal = ({ arquivoId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !arquivoId) return;
    setLoading(true);
    setData(null);
    api
      .get(`/ocoren/arquivos/${arquivoId}/avaliacao`)
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [open, arquivoId]);

  const ev = data?.arquivo?.evaluation || {};
  const arquivo = data?.arquivo || {};
  const amostras = ev.amostras_problemas || [];
  const registros = ev.registros_por_tipo || {};

  const integridadeOk =
    (ev.linhas_corrompidas_notacao_cientifica || 0) === 0 &&
    (ev.descricoes_truncadas || 0) === 0 &&
    (ev.cnpjs_invalidos || 0) === 0 &&
    (ev.datas_invalidas || 0) === 0 &&
    Object.keys(ev.registros_desconhecidos || {}).length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="avaliacao-modal"
        className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <ScanLine size={22} />
            Avaliação Estrutural
          </DialogTitle>
          <DialogDescription className="text-sm">
            {arquivo.nome_arquivo || ""}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-sm text-[#52525B] py-6 text-center">
            Analisando arquivo…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-5 mt-2">
            {/* Status geral */}
            <div
              className={`flex items-center gap-3 p-3 rounded-md border ${
                integridadeOk
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-amber-50 border-amber-200"
              }`}
            >
              {integridadeOk ? (
                <CheckCircle2 size={18} className="text-emerald-700" />
              ) : (
                <AlertTriangle size={18} className="text-amber-700" />
              )}
              <div
                className={`text-sm font-semibold ${
                  integridadeOk ? "text-emerald-900" : "text-amber-900"
                }`}
              >
                {integridadeOk
                  ? "Arquivo íntegro — nenhum problema estrutural detectado"
                  : "Arquivo com avisos — veja detalhes abaixo"}
              </div>
            </div>

            {/* Grid de estatísticas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat
                label="Linhas Totais"
                value={ev.total_linhas ?? 0}
              />
              <Stat
                label="Processadas"
                value={ev.linhas_processadas ?? 0}
                accent="success"
              />
              <Stat
                label="Ignoradas"
                value={ev.linhas_ignoradas ?? 0}
              />
              <Stat
                label="Movs. extraídas"
                value={arquivo.total_movimentacoes ?? 0}
              />
              <Stat
                label="Notação científica"
                value={ev.linhas_corrompidas_notacao_cientifica ?? 0}
                accent={
                  (ev.linhas_corrompidas_notacao_cientifica || 0) > 0
                    ? "warning"
                    : "default"
                }
              />
              <Stat
                label="Descrições truncadas"
                value={ev.descricoes_truncadas ?? 0}
                accent={
                  (ev.descricoes_truncadas || 0) > 0 ? "warning" : "default"
                }
              />
              <Stat
                label="CNPJs inválidos"
                value={ev.cnpjs_invalidos ?? 0}
                accent={(ev.cnpjs_invalidos || 0) > 0 ? "error" : "default"}
              />
              <Stat
                label="Datas inválidas"
                value={ev.datas_invalidas ?? 0}
                accent={(ev.datas_invalidas || 0) > 0 ? "error" : "default"}
              />
            </div>

            {/* Registros por tipo */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold mb-2 flex items-center gap-1.5">
                <Sigma size={11} />
                Registros por tipo
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(registros).map(([k, v]) => (
                  <span
                    key={k}
                    className="text-xs px-2 py-1 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm font-mono-data"
                  >
                    <span className="font-bold text-[#09090B]">{k}</span>
                    <span className="text-[#52525B] ml-1.5">×{v}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Warnings consolidados */}
            {(arquivo.warnings || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold mb-2">
                  Avisos
                </div>
                <ul className="space-y-1 text-sm text-[#09090B]">
                  {arquivo.warnings.map((w, i) => (
                    <li
                      key={i}
                      className="flex gap-2 items-start"
                      data-testid={`avaliacao-warning-${i}`}
                    >
                      <AlertTriangle
                        size={13}
                        className="text-amber-600 mt-0.5 shrink-0"
                      />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Amostras de problemas */}
            {amostras.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold mb-2">
                  Amostras de linhas problemáticas
                </div>
                <div className="max-h-60 overflow-y-auto border border-[#E4E4E7] rounded-md">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAFAFA] text-[#52525B] uppercase tracking-[0.1em] text-[10px]">
                      <tr>
                        <th className="px-3 py-2 text-left w-12">Linha</th>
                        <th className="px-3 py-2 text-left w-36">Tipo</th>
                        <th className="px-3 py-2 text-left">Motivo / Trecho</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amostras.map((a, i) => (
                        <tr
                          key={i}
                          className="border-t border-[#E4E4E7]"
                          data-testid={`avaliacao-amostra-${i}`}
                        >
                          <td className="px-3 py-2 font-mono-data text-[#09090B] font-semibold">
                            {a.linha}
                          </td>
                          <td className="px-3 py-2 text-[#52525B]">
                            {a.tipo}
                          </td>
                          <td className="px-3 py-2 text-[#09090B]">
                            <div>{a.motivo}</div>
                            <div className="text-[10px] text-[#52525B] mt-1 font-mono-data truncate">
                              {a.trecho}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AvaliacaoModal;
